import { useState, useEffect } from 'preact/hooks';
import { api } from './api';
import { getEffectiveRealtimeEndDate } from './dateRange';
import { appendDashboardFiltersToSearchParams, dashboardFilters, dateRangePreset, endDate, isDashboardFilterActive, selectedProperty, startDate, toggleDashboardFilter } from './state';
import type { DashboardFilterField } from './state';

type SourceRow = {
    referrer: string;
    visits: number;
};

const SOURCE_DISPLAY_NAMES: Record<string, string> = {
    bing: 'Bing',
    brave: 'Brave',
    duckduckgo: 'DuckDuckGo',
    facebook: 'Facebook',
    github: 'GitHub',
    google: 'Google',
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    reddit: 'Reddit',
    twitter: 'X',
    't.co': 'X',
    x: 'X',
    youtube: 'YouTube',
};

const SOURCE_FAVICON_DOMAINS: Record<string, string> = {
    bing: 'bing.com',
    brave: 'brave.com',
    chatgpt: 'chatgpt.com',
    duckduckgo: 'duckduckgo.com',
    facebook: 'facebook.com',
    github: 'github.com',
    google: 'google.com',
    instagram: 'instagram.com',
    linkedin: 'linkedin.com',
    reddit: 'reddit.com',
    twitter: 'x.com',
    't.co': 'x.com',
    x: 'x.com',
    youtube: 'youtube.com',
};

type SourcesProps = {
    liveRefreshTick?: number;
};

const Sources = ({ liveRefreshTick = 0 }: SourcesProps) => {
    const [sourcesData, setSourcesData] = useState<SourceRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [groupBy, setGroupBy] = useState('source');
    const [showDialog, setShowDialog] = useState(false);
    const [dialogData, setDialogData] = useState<SourceRow[]>([]);
    const [totalVisits, setTotalVisits] = useState(0);

    const buildSourcesUrl = (limit?: number) => {
        const params = new URLSearchParams({
            property: selectedProperty.value,
            start: startDate.value,
            end: getEffectiveRealtimeEndDate(endDate.value),
            groupBy,
        });
        appendDashboardFiltersToSearchParams(params);

        if (typeof limit === 'number') {
            params.set('limit', String(limit));
        }

        return `/sources?${params.toString()}`;
    };

    const loadSources = async (options?: { limit?: number; showLoading?: boolean; updateDialog?: boolean }) => {
        if (!selectedProperty.value) {
            return;
        }

        const { limit, showLoading = true, updateDialog = false } = options ?? {};

        if (showLoading) {
            setLoading(true);
        }

        const result = await api(buildSourcesUrl(limit));

        if (updateDialog) {
            setDialogData(result.data);
            return;
        }

        setSourcesData(result.data);
        setTotalVisits(result.totalVisits);
        setLoading(false);
    };

    const getTitle = () => {
        switch (groupBy) {
            case 'channel':
                return 'Canali';
            case 'utm_source':
                return 'UTM Source';
            case 'utm_medium':
                return 'UTM Medium';
            case 'utm_campaign':
                return 'UTM Campaign';
            default:
                return 'Fonti di traffico';
        }
    };

    const getEmptyValueLabel = () => {
        return groupBy === 'source' ? '(Diretto)' : '(Non impostato)';
    };

    const getColumnLabel = () => {
        switch (groupBy) {
            case 'channel':
                return 'Canale';
            case 'utm_source':
                return 'UTM Source';
            case 'utm_medium':
                return 'UTM Medium';
            case 'utm_campaign':
                return 'UTM Campaign';
            default:
                return 'Fonte';
        }
    };

    const getFilterField = (): DashboardFilterField => {
        switch (groupBy) {
            case 'channel':
                return 'channel';
            case 'utm_source':
                return 'utm_source';
            case 'utm_medium':
                return 'utm_medium';
            case 'utm_campaign':
                return 'utm_campaign';
            default:
                return 'source';
        }
    };

    const isDirectSource = (value: string) => {
        const normalized = value.trim().toLowerCase();
        return normalized === '(direct)' || normalized === '(diretto)' || normalized === 'direct' || normalized === 'direct / none';
    };

    const normalizeSourceHost = (value: string) => {
        const normalized = value.trim().toLowerCase();

        if (!normalized || isDirectSource(normalized)) {
            return null;
        }

        const withoutProtocol = normalized.replace(/^https?:\/\//, '');
        const withoutPath = withoutProtocol.split('/')[0] ?? withoutProtocol;
        const withoutPort = withoutPath.split(':')[0] ?? withoutPath;
        const withoutWww = withoutPort.replace(/^www\./, '');

        return withoutWww || null;
    };

    const getSourceDisplayLabel = (value: string) => {
        if (groupBy !== 'source') {
            return value || getEmptyValueLabel();
        }

        if (isDirectSource(value)) {
            return 'Direct / None';
        }

        const normalizedHost = normalizeSourceHost(value);
        if (!normalizedHost) {
            return getEmptyValueLabel();
        }

        return SOURCE_DISPLAY_NAMES[normalizedHost] ?? normalizedHost;
    };

    const getSourceFaviconDomain = (value: string) => {
        if (groupBy !== 'source') {
            return null;
        }

        const normalizedHost = normalizeSourceHost(value);
        if (!normalizedHost) {
            return null;
        }

        return SOURCE_FAVICON_DOMAINS[normalizedHost] ?? (normalizedHost.includes('.') ? normalizedHost : null);
    };

    const renderSourceValue = (value: string) => {
        const label = getSourceDisplayLabel(value);
        const faviconDomain = getSourceFaviconDomain(value);
        const fallbackLabel = label.charAt(0).toUpperCase();

        return (
            <span className="source-cell-content">
                {groupBy === 'source' ? (
                    faviconDomain ? (
                        <img
                            className="source-favicon"
                            src={`https://icons.duckduckgo.com/ip3/${encodeURIComponent(faviconDomain)}.ico`}
                            alt=""
                            loading="lazy"
                            referrerPolicy="no-referrer"
                        />
                    ) : (
                        <span className="source-fallback-icon" aria-hidden="true">
                            {fallbackLabel}
                        </span>
                    )
                ) : null}
                <span className="source-cell-text">{label}</span>
            </span>
        );
    };
    
    useEffect(() => {
        if (selectedProperty.value) {
            loadSources({ showLoading: true }).catch(error => {
                console.error('Error fetching sources data:', error);
                setLoading(false);
            });
        }
    }, [selectedProperty.value, startDate.value, endDate.value, groupBy, JSON.stringify(dashboardFilters.value)]);

    useEffect(() => {
        if (!selectedProperty.value) {
            return;
        }

        if (dateRangePreset.value !== 'live' || liveRefreshTick === 0) {
            return;
        }

        loadSources({ showLoading: false }).catch(error => {
            console.error('Error refreshing sources data:', error);
        });
    }, [liveRefreshTick, groupBy, JSON.stringify(dashboardFilters.value)]);

    const openDialog = () => {
        setShowDialog(true);
        loadSources({ limit: 1000, showLoading: false, updateDialog: true }).catch(error => {
            console.error('Error fetching full sources data:', error);
        });
    };

    const renderTable = (data: typeof sourcesData, showProgress = false) => (
        <div className="sources-list" role="table" aria-label={getTitle()}>
            <div className="sources-list-row sources-list-header" role="row">
                <div className="sources-list-cell sources-list-cell-source" role="columnheader">{getColumnLabel()}</div>
                <div className="sources-list-cell sources-list-cell-number" role="columnheader">Visite</div>
                <div className="sources-list-cell sources-list-cell-percentage" role="columnheader">%</div>
            </div>
            <div role="rowgroup">
                {data.map((source) => {
                    const percentage = totalVisits > 0 ? ((source.visits / totalVisits) * 100) : 0;
                    const percentageStr = percentage.toFixed(1);
                    const style = showProgress ? { '--progress-width': `${percentage}%` } as any : undefined;
                    const filterField = getFilterField();
                    return (
                        <div
                            key={source.referrer}
                            className={`sources-list-row filterable-row ${isDashboardFilterActive(filterField, source.referrer) ? 'is-filter-active' : ''}`}
                            role="row"
                            style={style}
                            onClick={() => toggleDashboardFilter(filterField, source.referrer)}
                        >
                            <div
                                className="sources-list-cell sources-list-cell-source table-cell-primary source-cell"
                                role="cell"
                                title={getSourceDisplayLabel(source.referrer)}
                            >
                                {renderSourceValue(source.referrer)}
                            </div>
                            <div className="sources-list-cell sources-list-cell-number table-cell-number" role="cell">
                                {source.visits}
                            </div>
                            <div className="sources-list-cell sources-list-cell-percentage table-cell-percentage" role="cell">
                                {percentageStr}%
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <>
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">{getTitle()}</h3>
                    <select
                        className="select-input card-select"
                        value={groupBy}
                        onChange={(e) => setGroupBy((e.target as HTMLSelectElement).value)}
                    >
                        <option value="channel">Canali</option>
                        <option value="source">Fonti di traffico</option>
                        <option value="utm_source">UTM Source</option>
                        <option value="utm_medium">UTM Medium</option>
                        <option value="utm_campaign">UTM Campaign</option>
                    </select>
                </div>
                {loading ? (
                    <div className="loading">
                        <div className="loading-spinner"></div>
                    </div>
                ) : sourcesData.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">📊</div>
                        <p className="empty-state-text">Nessun dato disponibile</p>
                    </div>
                ) : (
                    <>
                        {renderTable(sourcesData, true)}
                        <button className="details-button" onClick={openDialog}>
                            Visualizza tutti i dettagli
                        </button>
                    </>
                )}
            </div>

            {showDialog && (
                <div className="dialog-overlay" onClick={() => setShowDialog(false)}>
                    <div className="dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h2 className="dialog-title">{getTitle()} - Dettagli completi</h2>
                            <button className="dialog-close" onClick={() => setShowDialog(false)}>×</button>
                        </div>
                        <div className="dialog-body">
                            {renderTable(dialogData, true)}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Sources;
